import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, Languages } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useBusiness } from "@/context/BusinessContext";
import { Topbar } from "@/components/Topbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Plans() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { business, updateBusiness } = useBusiness();
  const navigate = useNavigate();
  const ar = lang === "ar";
  const isManager = user?.role === "manager";

  const plans = [
    {
      id: "free", name: ar ? "مجاني" : "Free", price: "$0",
      tagline: ar ? "للأفراد" : "For individuals",
      features: ar
        ? ["ترجمة نصية فورية", "ترجمة الرسائل الصوتية", "كشف تلقائي للغة", "سجل الترجمات", "إضافة المتصفح"]
        : ["Instant text translation", "Voice note translation", "Auto language detection", "Translation history", "Browser extension"],
      cta: ar ? "ابدأ مجاناً" : "Start free", highlight: false,
    },
    {
      id: "pro", name: "Pro", price: "$29",
      tagline: ar ? "للفرق الصغيرة" : "For small teams",
      features: ar
        ? ["كل مزايا المجاني", "صندوق وارد مشترك", "حتى 3 وكلاء", "ردود محفوظة", "ملاحظات ووسوم العملاء", "تحليلات أساسية"]
        : ["Everything in Free", "Shared team inbox", "Up to 3 agents", "Saved replies", "CRM notes & tags", "Basic analytics"],
      cta: ar ? "اختر Pro" : "Choose Pro", highlight: true,
    },
    {
      id: "business", name: ar ? "أعمال" : "Business", price: "$99",
      tagline: ar ? "للأعمال المتنامية" : "For growing businesses",
      features: ar
        ? ["كل مزايا Pro", "وكلاء غير محدودين", "ترجمة تلقائية للوارد", "ربط واتساب للأعمال", "تحليلات متقدمة", "متابعة العملاء والمواعيد", "دعم ذو أولوية"]
        : ["Everything in Pro", "Unlimited agents", "Auto-translate incoming", "WhatsApp Business API", "Advanced analytics", "Lead & appointment tracking", "Priority support"],
      cta: ar ? "اختر أعمال" : "Choose Business", highlight: false,
    },
  ];

  const go = async (planId) => {
    if (isManager) {
      try {
        await updateBusiness({ plan: planId });
        toast.success(t("planUpdated"));
        navigate("/learning");
        return;
      } catch {
        toast.error("Could not update plan");
        return;
      }
    }
    navigate(user ? (user.role === "individual" ? "/app" : "/inbox") : "/register");
  };

  return (
    <div className="min-h-screen bg-background">
      {user ? <Topbar /> : (
        <header className="max-w-7xl mx-auto px-5 h-20 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center text-white"><Languages size={22} /></div>
            <span className="font-head font-bold text-xl text-primary">{t("appName")}</span>
          </button>
          <Button variant="ghost" onClick={() => navigate("/login")}>{t("login")}</Button>
        </header>
      )}

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="font-head font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-foreground">{t("pricingTitle")}</h1>
          <p className="text-muted-foreground mt-3 text-lg">{t("pricingSub")}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`relative rounded-3xl border p-7 flex flex-col ${p.highlight ? "border-accent bg-white shadow-xl scale-[1.02]" : "border-border bg-white"}`}
            >
              {p.highlight && (
                <span className="absolute -top-3 start-7 bg-accent text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {ar ? "الأكثر شيوعاً" : "Most popular"}
                </span>
              )}
              <div className="font-head font-bold text-xl text-foreground">{p.name}</div>
              <div className="text-sm text-muted-foreground">{p.tagline}</div>
              <div className="mt-4 mb-6">
                <span className="font-head font-extrabold text-4xl text-foreground">{p.price}</span>
                {p.id !== "free" && <span className="text-muted-foreground">{t("perMonth")}</span>}
              </div>
              <ul className="space-y-3 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/90">
                    <Check size={17} className="text-accent shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => go(p.id)}
                disabled={business?.plan === p.id}
                data-testid={`plan-cta-${p.id}`}
                className={`mt-7 h-12 rounded-xl text-base ${p.highlight ? "bg-accent hover:bg-accent/90" : "bg-primary hover:bg-primary/90"} ${business?.plan === p.id ? "opacity-60" : ""}`}
              >
                {business?.plan === p.id ? t("currentPlan") : p.cta}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-muted-foreground mt-8">
          {ar ? "الدفع والاشتراكات ستُفعّل قريباً." : "Billing & subscriptions activate soon — explore everything free for now."}
        </p>
      </div>
    </div>
  );
}
